import axios from 'axios';
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { endpoints } from '@/api/endpoints';
import { urls } from '@/config/urls';

export type NetworkStatus = 'checking' | 'available' | 'unavailable';

interface NetworkStatusContextValue {
  status: NetworkStatus;
  hasConnectedOnce: boolean;
}

const DEFAULT_POLL_INTERVAL_MS = 60000;
// Lambda cold starts can take several seconds to respond to the first invocation
// after being idle, so the probe timeout must comfortably exceed that delay.
const PROBE_TIMEOUT_MS = 15000;
// Multiple consecutive failures are required before reporting unavailable, so a
// single slow cold-start response doesn't trigger the error overlay.
const CONSECUTIVE_FAILURES_THRESHOLD = 3;
// Bounded fast retry while below the failure threshold: resolves transient blips
// quickly without waiting a full poll interval, and without spamming the endpoint.
const RETRY_DELAY_MS = 5000;

const pollIntervalMs = (() => {
  const configured = Number(import.meta.env.VITE_HEALTH_POLL_INTERVAL_MS);
  return Number.isFinite(configured) && configured > 0
    ? configured
    : DEFAULT_POLL_INTERVAL_MS;
})();

const NetworkStatusContext = createContext<NetworkStatusContextValue | null>(
  null,
);

// Bypasses apiClient's interceptors: they collapse "no response" (network-level)
// and HTTP error responses into the same shape, which would hide the distinction we need here.
async function isPrivateNetworkReachable(): Promise<boolean> {
  try {
    await axios.get(`${urls.apiBaseUrl}${endpoints.health}`, {
      timeout: PROBE_TIMEOUT_MS,
    });
    return true;
  } catch (error) {
    // Any HTTP response (even an error status) proves the request reached the gateway.
    return axios.isAxiosError(error) && Boolean(error.response);
  }
}

export function NetworkStatusProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<NetworkStatus>('checking');
  const [hasConnectedOnce, setHasConnectedOnce] = useState(false);
  const isMountedRef = useRef(true);
  const consecutiveFailuresRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;
    let timeoutId: ReturnType<typeof setTimeout>;

    const scheduleNext = (delayMs: number) => {
      timeoutId = setTimeout(() => void checkStatus(), delayMs);
    };

    const checkStatus = async () => {
      const reachable = await isPrivateNetworkReachable();

      if (!isMountedRef.current) {
        return;
      }

      if (reachable) {
        consecutiveFailuresRef.current = 0;
        setStatus('available');
        setHasConnectedOnce(true);
        scheduleNext(pollIntervalMs);
        return;
      }

      consecutiveFailuresRef.current += 1;
      if (consecutiveFailuresRef.current >= CONSECUTIVE_FAILURES_THRESHOLD) {
        setStatus('unavailable');
        scheduleNext(pollIntervalMs);
      } else {
        scheduleNext(RETRY_DELAY_MS);
      }
    };

    void checkStatus();

    return () => {
      isMountedRef.current = false;
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <NetworkStatusContext.Provider value={{ status, hasConnectedOnce }}>
      {children}
    </NetworkStatusContext.Provider>
  );
}

export function useNetworkStatus(): NetworkStatusContextValue {
  const context = useContext(NetworkStatusContext);
  if (!context) {
    throw new Error(
      'useNetworkStatus must be used within a NetworkStatusProvider',
    );
  }
  return context;
}
